<template>
  <ion-page>
    <main-header :back-href="backHref"></main-header>
    <ion-content padding>
      <loading-spinner v-if="loading"></loading-spinner>
      <div v-else>
        <h3 class="ion-no-margin">Name</h3>
        <ion-list>
          <input-box
            :validator="$v.displayName"
            :error-messages="{
              required: 'Please enter your name.',
              maxLength: 'Please limit your name to 64 characters.'
            }"
            :required="true"
            v-model="displayName"
            maxlength="64"
          >
            Your Name
          </input-box>
        </ion-list>

        <h3 class="ion-no-margin">Payment</h3>
        <ion-grid>
          <ion-row>
            <ion-col size="8">
              <ion-list>
                <ion-item>
                  <ion-label position="stacked">
                    Card
                    <ion-text color="danger">*</ion-text>
                  </ion-label>
                  <ion-select
                    :value="stripeCardId"
                    @ionChange="stripeCardId = $event.detail.value"
                  >
                    <ion-select-option
                      v-for="stripeCard in stripeCards"
                      :key="`stripeCard-${stripeCard.id}`"
                      :value="stripeCard.id"
                    >
                      {{ stripeCard.brand | ucfirst }} -
                      {{ stripeCard.last4 }}
                    </ion-select-option>
                  </ion-select>
                </ion-item>
              </ion-list>
            </ion-col>
            <ion-col size="4">
              <div class="ion-text-end ion-margin-top">
                <ion-button
                  @click="handleAddNewCard()"
                  size="small"
                  color="light"
                  class="ion-margin-top"
                >
                  Add a New Card
                </ion-button>
              </div>
            </ion-col>
          </ion-row>
        </ion-grid>

        <div v-if="showFulfillmentPicker()" class="ion-padding-bottom">
          <ion-segment
            :value="fulfillment"
            @ionChange="changeFulfillment($event)"
          >
            <ion-segment-button :value="ORDER_FULFILLMENT_DELIVERY">
              <ion-label>Delivery</ion-label>
            </ion-segment-button>
            <ion-segment-button :value="ORDER_FULFILLMENT_PICKUP">
              <ion-label>Pick-up</ion-label>
            </ion-segment-button>
          </ion-segment>
        </div>

        <div
          v-if="
            location.fulfillmentDelivery &&
              fulfillment === ORDER_FULFILLMENT_DELIVERY
          "
        >
          <h3 class="ion-no-margin">
            Location
          </h3>
          <ion-grid>
            <ion-row>
              <ion-col>
                <ion-list>
                  <input-box
                    :validator="$v.section"
                    :error-messages="{
                      required: 'Please enter your section.',
                      maxLength: 'Please limit the section to 10 characters.'
                    }"
                    :required="true"
                    v-model="section"
                    maxlength="10"
                  >
                    Section
                  </input-box>
                </ion-list>
              </ion-col>
              <ion-col>
                <ion-list>
                  <input-box
                    :validator="$v.row"
                    :error-messages="{
                      required: 'Please enter your row.',
                      maxLength: 'Please limit the row to 10 characters.'
                    }"
                    :required="true"
                    v-model="row"
                    maxlength="10"
                  >
                    Row
                  </input-box>
                </ion-list>
              </ion-col>
              <ion-col>
                <ion-list>
                  <input-box
                    :validator="$v.seat"
                    :error-messages="{
                      required: 'Please enter your seat.',
                      maxLength: 'Please limit the seat to 10 characters.'
                    }"
                    :required="true"
                    v-model="seat"
                    maxlength="10"
                  >
                    Seat
                  </input-box>
                </ion-list>
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col>
                <ion-list>
                  <input-box
                    :validator="$v.instructions"
                    v-model="instructions"
                    :error-message="{
                      maxLength:
                        'Please limit the instructions to 255 characters.'
                    }"
                    maxlength="255"
                  >
                    Other tips to find you...
                  </input-box>
                </ion-list>
              </ion-col>
            </ion-row>
          </ion-grid>
        </div>
        <div
          v-if="
            location.fulfillmentPickup &&
              fulfillment === ORDER_FULFILLMENT_PICKUP
          "
        >
          <h3 class="ion-no-margin">
            Pick-up Directions
          </h3>
          <p>
            {{ location.pickupInstructions }}
          </p>
        </div>
        <form @submit.prevent="handleSubmit()">
          <submit-button :submitting="submitting">
            Submit Order
          </submit-button>
        </form>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import StripeCard from "@/models/StripeCard";
import submittable from "@/mixins/submittable";
import { required, requiredIf, maxLength } from "vuelidate/lib/validators";
import errorAlert from "@/ErrorAlert";
import filterOrder from "@/mixins/filter-order";
import {
  ORDER_FULFILLMENT_DELIVERY,
  ORDER_FULFILLMENT_PICKUP
} from "@/constants/order";

export default {
  title: "Review & Confirm", // still the same name as previous one

  mixins: [submittable, filterOrder],

  data: function() {
    return {
      loading: true,
      displayName: "",
      stripeCards: [],
      stripeCardId: "",
      section: "",
      row: "",
      seat: "",
      instructions: "",
      fulfillment: ORDER_FULFILLMENT_DELIVERY,
      ORDER_FULFILLMENT_DELIVERY,
      ORDER_FULFILLMENT_PICKUP
    };
  },

  created() {
    this.displayName = this.user.displayName;

    if (!this.showFulfillmentPicker() && this.location.fulfillmentPickup) {
      this.fulfillment = ORDER_FULFILLMENT_PICKUP;
    }

    this.$firestore()
      .collection("users")
      .doc(this.user.uid)
      .collection("stripeCards")
      .withConverter(StripeCard.firebaseConverter)
      .get()
      .then(snapshot => {
        let defaultId;
        snapshot.forEach(card => {
          let stripeCard = card.data();
          if (stripeCard.isDefault) {
            defaultId = stripeCard.id;
          }

          this.stripeCards.push(stripeCard);
        });

        if (this.$route.params.stripeCardId) {
          // used when adding a new one
          this.stripeCardId = this.$route.params.stripeCardId;
        } else if (defaultId) {
          // this when we have a default one
          this.stripeCardId = defaultId;
        } else {
          // in the rare case we don't have a default one
          this.stripeCardId = this.stripeCards[0].id;
        }

        this.loading = false;
      });
  },

  methods: {
    ...mapMutations("order", [
      "setCompletedOrderId",
      "addItemToOrder",
      "clearOrder"
    ]),

    ...mapActions("auth", ["updateProfile"]),

    handleAddNewCard() {
      this.$router.push({ name: "App: Order Add Card" }).catch(e => e);
    },

    async handleSubmit() {
      this.startSubmission();

      this.$v.$touch();

      if (!this.$v.$invalid) {
        const filteredOrderItems = await this.filterOrderItemsByAvailable(
          this.orderItems,
          this.location.id
        );

        if (filteredOrderItems.length !== this.orderItems.length) {
          this.infoToast(
            "There were some items in this order that are no longer available."
          );

          this.clearOrder();
          filteredOrderItems.forEach(item =>
            this.addItemToOrder({
              id: item.id,
              quantity: item.quantity,
              instructions: item.instructions,
              optionValueId: item.optionValueId
            })
          );

          this.$router
            .push({ name: "App: Order Review and Confirm" })
            .catch(e => e);
          return;
        }

        let submitOrder = this.$functions().httpsCallable("submitOrder");

        submitOrder({
          requested: new Date().toISOString(),
          locationId: this.location.id,
          orderItems: this.orderItems,
          displayName: this.displayName,
          stripeCardId: this.stripeCardId,
          fulfillment: this.fulfillment,
          section: this.section,
          row: this.row,
          seat: this.seat,
          instructions: this.instructions
        })
          .then(id => {
            this.setCompletedOrderId(id);
            if (!this.user.displayName) {
              this.updateProfile({ displayName: this.displayName });
            }
            this.$router.push({ name: "App: Order Submitted" }).catch(e => e);
          })
          .catch(e => {
            errorAlert(e.message);

            this.completeSubmission();
          });
      } else {
        this.completeSubmission();
      }
    },

    showFulfillmentPicker() {
      return (
        this.location.fulfillmentDelivery && this.location.fulfillmentPickup
      );
    },

    changeFulfillment(event) {
      this.fulfillment = event.detail.value;
    }
  },

  computed: {
    ...mapGetters("auth", ["user"]),

    ...mapGetters("order", {
      orderItems: "orderItems",
      location: "orderLocation"
    }),

    backHref() {
      return this.$router.resolve({
        name: "App: Order Review and Confirm"
      }).href;
    }
  },

  validations: {
    displayName: {
      required,
      maxLength: maxLength(64)
    },

    // could def do more validation I'm sure
    stripeCardId: {
      required
    },

    section: {
      required: requiredIf(function() {
        return this.fulfillment === ORDER_FULFILLMENT_DELIVERY;
      }),
      maxLength: maxLength(10)
    },

    row: {
      required: requiredIf(function() {
        return this.fulfillment === ORDER_FULFILLMENT_DELIVERY;
      }),
      maxLength: maxLength(10)
    },

    seat: {
      required: requiredIf(function() {
        return this.fulfillment === ORDER_FULFILLMENT_DELIVERY;
      }),
      maxLength: maxLength(10)
    },

    instructions: {
      maxLength: maxLength(255)
    }
  }
};
</script>
